"use client";

import type { Product } from "@/app/directories/shop/helper";
import { getBuyNowDestinationPath } from "@/app/directories/shop/lib/storefrontLogicHelper";
import { formatAsDollars } from "@/app/directories/shop/lib/formatAsDollars";
import { useTranslations } from "@/lib/next-intl-typed";
import { useInsuranceGatedToggle } from "@frontend/components/InsuranceConnector/insurance-connector-provider";
import { PriceToggle } from "@frontend/components/priceToggle/priceToggle";
import { getAllEnabledOrgConfigFlagsByOrgId } from "@frontend/lib/actions/actions";
import { getOrganizationAssetsForOrg } from "@frontend/lib/actions/config-actions";
import type { Hrt } from "@frontend/lib/graphql/generated-gql";
import {
  type GetProductsByCategoryIdQuery,
  ProductType,
} from "@frontend/lib/graphql/generated-gql";
import { useBoundStore } from "@frontend/lib/stores/globalStore";
import { Button as MovingBorderButton } from "@frontend/shared/components/ui/moving-border";
import { useIsFetching, useQuery } from "@tanstack/react-query";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { PriceExplainabilityRenderer } from "../PriceExplainability/PriceExplainabilityRenderer";
import { EligibilityButton } from "../eligibility/EligibilityButton";
import { getPurchaseCtaLayout } from "../eligibility/purchase-cta-layout";
import { ProductOptionsContainer } from "../featuredCarousel/ProductOptionsContainer";
import { PendingPricesMessage } from "../pendingPrices/PendingPricesMessage";
import { usePriceMap } from "../priceToggle/usePriceMap";

interface TreatmentProps {
  product: Product;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  hrts: any;
  description: string;
  name: string;
  productId: string;
  clientSlug: string;
  priceToggleColor: string;
  displayMode: "accordion" | "grid" | "default";
  orgId: number;
}

const truncateToSentences = (text, maxSentences = 3) => {
  // Split into sentences (handles multiple sentence-ending punctuation)
  const sentences = text.match(/[^.!?]+[.!?]+/g) || [];

  if (sentences.length <= maxSentences) {
    return text;
  }

  // Join first maxSentences sentences and add ellipsis
  return `${sentences.slice(0, maxSentences).join("")}`;
};

export function CategoryTreatmentWrapper({
  product,
  hrts,
  description,
  name,
  productId,
  clientSlug,
  priceToggleColor,
  displayMode,
  orgId,
}: TreatmentProps) {
  const {
    createGatedHandler,
    isInsuranceConnected,
    insuranceState,
    sendInsurance,
  } = useInsuranceGatedToggle();
  const {
    isInsuredMode,
    handlePriceModeChange,
    lowestPrice,
    lowestComparisonPrice,
    isToggleDisabled,
    isSelfPayOnly,
  } = usePriceMap(hrts);

  const handlePriceModeChangeWithInsuranceCheck = createGatedHandler(
    handlePriceModeChange,
  );

  const pricingStatus = useBoundStore.use.pricingStatus();
  const isFetchingPrice = !!useIsFetching({ queryKey: ["updatePrices"] });
  const pricesLoaded = pricingStatus === "SUCCESS" && !isFetchingPrice;
  const isPendingPricesMode = useBoundStore.use.isPendingPricesMode();

  const t = useTranslations("Shop");
  const tStorefront = useTranslations("StorefrontInsurance");

  const { data: configFlags, isPending: isConfigFlagsPending } = useQuery({
    queryKey: ["configFlags", orgId],
    queryFn: () => getAllEnabledOrgConfigFlagsByOrgId(orgId),
    enabled: !!orgId,
  });

  const eligibilityCheckResult = useBoundStore.use.eligibilityCheckResult();
  const isInsurable = !isSelfPayOnly && !configFlags?.no_insured_procedures;
  // Pending prices mode means the user has connected insurance but we couldn't
  // verify it — the storefront commits to treating the transaction as insured
  // anyway, so the UI should behave as if insurance were fully connected
  // (hide Connect Insurance, render Book Now in the primary/black state).
  const isInsuranceEffectivelyConnected =
    isInsuranceConnected || isPendingPricesMode;
  const orgName = insuranceState.context.orgName;
  const {
    showConnectInsurance,
    bookNowWidth,
    buttonHeightClass,
    connectInsuranceOuterHeightClass,
  } = getPurchaseCtaLayout({
    isInsurable,
    isInsuranceConnected: isInsuranceEffectivelyConnected,
    isFetchingPrice,
    eligibilityResult: eligibilityCheckResult.result,
    isEligibilitySystemOn: !!configFlags?.storefront_offboard_consumers,
    layout: "stacked",
  });

  const router = useRouter();
  const [selectedProductId, setSelectedProductId] = useState<number | null>(
    null,
  );

  const { data: organizationAssets } = useQuery({
    queryKey: ["organizationAssets", orgId],
    queryFn: () => getOrganizationAssetsForOrg(orgId),
    enabled: !!orgId,
  });
  const primaryColor = organizationAssets?.primaryColor ?? "#000000";

  const isPdpAndBuyNow = product.product_type === ProductType.PdpAndBuyNow;
  const isBuyNowType =
    product.product_type === ProductType.BuyNow || isPdpAndBuyNow;

  const handleBuyNowClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!isBuyNowType) return;
    const hrt = hrts[0]?.hrt;
    const hrtId = hrt?.hrtId;
    if (hrts.length > 1) {
      setSelectedProductId(product.id);
      return;
    }
    // configFlags drives the schedule-vs-review routing decision; bail until
    // the query settles so an eager click can't silently land on /review.
    if (isConfigFlagsPending) return;
    router.push(
      getBuyNowDestinationPath({
        hrt,
        orgId,
        configFlags,
        clientSlug,
        hrtId,
      }),
    );
  };

  /**
   * Currently for extension only, but willing to change it if design is ok
   */
  if (displayMode === "grid") {
    return (
      <div className="flex lg:flex-row sm:flex-col lg:gap-8 sm:gap-2 items-center">
        {product?.displayMetadata?.imageUrl && (
          <Image
            src={product?.displayMetadata?.imageUrl}
            alt={product.name}
            className="lg:h-[180px] lg:min-w-[180px] sm:h-[240px] sm:w-[240px] rounded-sm object-center object-cover"
            width={240}
            height={240}
          />
        )}

        <div className="w-full flex flex-col gap-3">
          <p className="config-h5 lg:!text-[20px] sm:!text-sm sm:!leading-4  min-h-[32px] font-light">
            {name}
          </p>
          <p className="config-p3 lg:!text-sm sm:!text-xs lg:!leading-4 min-h-[64px]">
            {description}
          </p>
          <p className="text-xs font-light">
            {hrts.length > 1 && <span>{t("StartingAtCategories")} </span>}
            <span className="font-mono">{formatAsDollars(lowestPrice, t)}</span>
            {!configFlags?.no_insured_procedures &&
              t("WithoutInsuranceCategories")}
            {isPendingPricesMode && <PendingPricesMessage className="inline" />}
          </p>
          {isBuyNowType ? (
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={handleBuyNowClick}
                className="rounded-full bg-black text-white lg:py-2 sm:py-2 w-[100px]"
              >
                <p className="config-p2 !text-xs text-center whitespace-nowrap">
                  {t("BuyNow")}
                </p>
              </button>
              {isPdpAndBuyNow && (
                <Link
                  href={`/${clientSlug}/products/${productId}`}
                  className="w-[100px] text-center"
                >
                  <p className="config-p2 !text-xs underline whitespace-nowrap">
                    {t("ViewDetails")}
                  </p>
                </Link>
              )}
            </div>
          ) : (
            <Link
              href={`/${clientSlug}/products/${productId}`}
              className="rounded-full bg-black text-white lg:py-2 sm:py-2 w-[100px]"
            >
              <p className="config-p2 !text-xs text-center whitespace-nowrap">
                {t("GetStarted")}
              </p>
            </Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 justify-between pb-14 ">
      <div className="flex flex-col lg:flex-row lg:items-start justify-between sm:gap-4 lg:gap-8 w-full">
        <div className="flex flex-col gap-3 w-full">
          <h2 className="config-h5 text-3xl font-bold">{name}</h2>
          <div className="sm:block lg:hidden my-2">
            <PriceToggle
              checked={isInsuredMode}
              onCheckedChange={handlePriceModeChangeWithInsuranceCheck}
              price={lowestPrice}
              disabled={isToggleDisabled}
              hasMultipleHrts={hrts.length > 1}
              isSelfPayOnlyClient={!!configFlags?.no_insured_procedures}
              isProductSelfPayOnly={isSelfPayOnly}
              isCategoryProduct={true}
              isCardCarousel={false}
              lowestComparisonPrice={lowestComparisonPrice}
              preDiscountprice={product?.displayMetadata?.preDiscount}
              pricesLoaded={pricesLoaded}
              priceToggleColor={undefined}
              isFetchingPrice={isFetchingPrice}
            />
          </div>
          <p className="config-p3 leading-relaxed lg:max-w-[450px] text-gray-800">
            {truncateToSentences(description, 1)}
          </p>
        </div>
        <div className="flex flex-col w-full max-w-[500px] gap-5 lg:items-end">
          <div className="sm:hidden lg:block w-full">
            <PriceToggle
              checked={isInsuredMode}
              onCheckedChange={handlePriceModeChangeWithInsuranceCheck}
              price={lowestPrice}
              disabled={isToggleDisabled}
              hasMultipleHrts={hrts.length > 1}
              isSelfPayOnlyClient={!!configFlags?.no_insured_procedures}
              isProductSelfPayOnly={isSelfPayOnly}
              isCategoryProduct={true}
              isCardCarousel={false}
              lowestComparisonPrice={lowestComparisonPrice}
              preDiscountprice={product?.displayMetadata?.preDiscount}
              pricesLoaded={pricesLoaded}
              priceToggleColor={priceToggleColor}
              isFetchingPrice={isFetchingPrice}
            />
          </div>

          <div className="w-full self-start">
            <PriceExplainabilityRenderer
              hrt={
                {
                  ...hrts[0].hrt,
                  orgId: orgId,
                } as unknown as Hrt
              }
            />
          </div>
          <div className="flex flex-col gap-2 w-full">
            {showConnectInsurance && (
              <MovingBorderButton
                hoverAnimation
                icon
                iconInline
                borderRadius="9999px"
                duration={8000}
                containerClassName={`w-full ${connectInsuranceOuterHeightClass}`}
                className="bg-black text-white border-black px-4 text-lg font-medium"
                onClick={() => sendInsurance({ type: "OPEN" })}
                aria-label={tStorefront("ConnectInsurance")}
              >
                {tStorefront("ConnectInsurance")}
              </MovingBorderButton>
            )}
            <EligibilityButton
              link={`/${clientSlug}/products/${productId}`}
              orgId={orgId}
              classNames={`${isPdpAndBuyNow ? "" : buttonHeightClass} flex items-center justify-center w-full ${
                bookNowWidth === "fit" && !isBuyNowType ? "md:w-fit" : ""
              }`}
            >
              {isBuyNowType ? (
                <div className="flex flex-col gap-2 w-full">
                  <button
                    type="button"
                    onClick={handleBuyNowClick}
                    className={`rounded-full ${buttonHeightClass} flex items-center justify-center border transition-all duration-200 w-full ${
                      isInsuranceEffectivelyConnected
                        ? "text-white border-transparent hover:bg-black hover:text-white"
                        : "border-black/40 text-black hover:bg-black hover:text-white hover:border-black"
                    }`}
                    style={
                      isInsuranceEffectivelyConnected
                        ? { backgroundColor: primaryColor || "#000000" }
                        : undefined
                    }
                  >
                    <p className="text-lg font-medium text-center whitespace-nowrap">
                      {t("BuyNow")}
                    </p>
                  </button>
                  {isPdpAndBuyNow && (
                    <Link
                      href={`/${clientSlug}/products/${productId}`}
                      className={`${buttonHeightClass} flex items-center justify-center w-full`}
                    >
                      <p
                        className="text-center font-medium text-lg underline hover:underline-offset-2 transition-all duration-300"
                        style={{ color: primaryColor || "#000000" }}
                      >
                        {t("ViewDetails")}
                      </p>
                    </Link>
                  )}
                </div>
              ) : (
                <Link
                  href={`/${clientSlug}/products/${productId}`}
                  className={`rounded-full border ${buttonHeightClass} flex items-center justify-center transition-all duration-200 w-full ${
                    bookNowWidth === "fit" ? "md:w-[140px]" : ""
                  } ${
                    isInsuranceEffectivelyConnected
                      ? "bg-black text-white border-black/40 hover:opacity-85"
                      : "bg-white text-black border-black/40 hover:bg-black hover:text-white"
                  }`}
                >
                  <p className="text-lg font-medium text-center whitespace-nowrap">
                    {t("BuyNow")}
                  </p>
                </Link>
              )}
            </EligibilityButton>
          </div>
          {/* Self-pay-only products never surface an insured price, so the
              pending-prices + connect-insurance messaging doesn't apply —
              suppress both rows when !isInsurable. The pending-prices copy
              also hides when the user has toggled over to self-pay, since
              they're looking at the cash price and don't need the "we'll
              review your coverage" note. */}
          {isInsurable && isPendingPricesMode && isInsuredMode ? (
            <div className="w-full self-start">
              <p>{tStorefront("OrgWillReviewCoverage", { orgName })}</p>
            </div>
          ) : showConnectInsurance ? (
            <div className="w-full self-start">
              <p>{tStorefront("ConnectToSeeRates")}</p>
            </div>
          ) : null}
        </div>
      </div>
      {isBuyNowType && (
        <ProductOptionsContainer
          product={
            product as unknown as GetProductsByCategoryIdQuery["findManyProducts"][number]
          }
          clientSlug={clientSlug}
          orgId={orgId}
          kioskMode={false}
          open={selectedProductId !== null}
          onOpenChange={(open) => {
            if (!open) setSelectedProductId(null);
          }}
        />
      )}
    </div>
  );
}
